import React from "react";
import { Link } from "react-router-dom";
import { ChevronRight, ShieldCheck } from "lucide-react";
import { useApp, LANGUAGES } from "@/context/AppContext";
import { YalolaLogo } from "@/components/YalolaLogo";
import { Flag } from "@/components/Flag";
import { StatusBarShim } from "@/components/StatusBarShim";
import { NavBarBackdrop } from "@/components/NavBarBackdrop";
import { acceptWelcome } from "@/lib/welcomeConsent";
import { requestAppTracking } from "@/lib/appTracking";

// Écran « Bienvenue » (app native, 1er lancement) — principe WhatsApp : logo seul en haut ; en bas, dans l'ordre,
// titre + slogan, règle « Tolérance zéro », phrase légale (Politique + CGU), langue, un seul bouton « Accepter et continuer ».
const L10N = {
  fr: { welcome: "Bienvenue sur {app}", slogan: "Le réseau chrétien qui rassemble.",
    zero_tolerance: "Tolérance zéro : aucun contenu inapproprié ni comportement abusif n'est toléré sur Yalola. Les contrevenants sont exclus.",
    legal: "Consulte notre {privacy}. Appuie sur « Accepter et continuer » pour accepter nos {terms}.",
    privacy: "Politique de confidentialité", terms: "Conditions générales d'utilisation", lang: "Langue", child_safety: "Sécurité des enfants", account_deletion: "Suppression de compte", accept: "Accepter et continuer" },
  en: { welcome: "Welcome to {app}", slogan: "The Christian network that unites.",
    zero_tolerance: "Zero tolerance: no objectionable content or abusive behaviour is tolerated on Yalola. Offenders are removed.",
    legal: "Read our {privacy}. Tap “Accept and continue” to accept our {terms}.",
    privacy: "Privacy Policy", terms: "Terms of Use", lang: "Language", child_safety: "Child safety", account_deletion: "Account deletion", accept: "Accept and continue" },
  es: { welcome: "Bienvenido a {app}", slogan: "La red cristiana que une.",
    zero_tolerance: "Tolerancia cero: en Yalola no se tolera ningún contenido inapropiado ni comportamiento abusivo. Los infractores son expulsados.",
    legal: "Consulta nuestra {privacy}. Pulsa «Aceptar y continuar» para aceptar nuestras {terms}.",
    privacy: "Política de privacidad", terms: "Condiciones de uso", lang: "Idioma", child_safety: "Seguridad infantil", account_deletion: "Eliminación de cuenta", accept: "Aceptar y continuar" },
  pt: { welcome: "Bem-vindo ao {app}", slogan: "A rede cristã que une.",
    zero_tolerance: "Tolerância zero: nenhum conteúdo impróprio nem comportamento abusivo é tolerado no Yalola. Os infratores são excluídos.",
    legal: "Consulta a nossa {privacy}. Toca em «Aceitar e continuar» para aceitar os nossos {terms}.",
    privacy: "Política de privacidade", terms: "Termos de utilização", lang: "Idioma", child_safety: "Segurança das crianças", account_deletion: "Eliminação de conta", accept: "Aceitar e continuar" },
};

const LegalLink = ({ to, testId, children }) => (
  <Link to={to} className="font-semibold underline" style={{ color: "var(--yalola-primary)" }} data-testid={testId}>{children}</Link>
);

const fill = (tpl, t) => tpl.split(/(\{privacy\}|\{terms\})/).map((seg, i) => {
  if (seg === "{privacy}") return <LegalLink key={i} to="/legal/privacy" testId="welcome-privacy-link">{t.privacy}</LegalLink>;
  if (seg === "{terms}") return <LegalLink key={i} to="/legal/terms" testId="welcome-terms-link">{t.terms}</LegalLink>;
  return seg;
});

export default function Welcome({ onAccept }) {
  const { lang, setLang, config, t: tr } = useApp();
  // Textes modifiables dans Admin › Traductions (clés welcome.*) ; L10N = valeurs par défaut / hors-ligne.
  const d = L10N[lang] || L10N.fr;
  const t = Object.fromEntries(Object.keys(d).map((k) => [k, tr(`welcome.${k}`, d[k])]));
  const appName = config?.settings?.app_name || "Yalola";
  const slogan = config?.settings?.auth_texts?.title?.[lang] || t.slogan;
  // iOS : l'invite système ATT (Apple) part ICI, dès l'acceptation des conditions — avant toute connexion,
  // avant UMP/AdMob et quel que soit le réglage admin des pubs. No-op Android/web, une seule fois par installation.
  const accept = () => { acceptWelcome(lang); onAccept?.(); requestAppTracking(); };

  return (
    <div className="min-h-[100dvh] relative flex flex-col" style={{ background: "var(--yalola-bg)", paddingTop: "var(--sa-top)", paddingBottom: "var(--sa-bottom)" }} data-testid="welcome-screen">
      <StatusBarShim />
      <div className="absolute inset-0 opacity-40 pointer-events-none" aria-hidden>
        <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full blur-3xl" style={{ background: "#F2C46D66" }} />
        <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full blur-3xl" style={{ background: "#D9775766" }} />
      </div>

      {/* Haut : le logo seul */}
      <div className="relative flex-1 flex items-center justify-center onb-fade-up">
        <YalolaLogo size={112} />
      </div>

      {/* Bas : tout ce qu'on accepte, puis le bouton */}
      <div className="relative px-6 pb-6 w-full max-w-md mx-auto text-center onb-fade-up onb-delay-1">
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight" style={{ fontFamily: "Outfit", color: "var(--yalola-text)" }} data-testid="welcome-title">
          {t.welcome.replace("{app}", appName)}
        </h1>
        <p className="mt-1.5 text-base md:text-lg" style={{ color: "var(--yalola-secondary)", fontFamily: "Outfit", fontWeight: 500 }} data-testid="welcome-slogan">{slogan}</p>

        <p className="mt-5 flex items-start gap-2 text-left text-xs leading-relaxed rounded-xl px-3 py-2.5" style={{ color: "var(--yalola-text)", background: "var(--yalola-surface)", border: "1px solid var(--yalola-border)" }} data-testid="welcome-zero-tolerance">
          <ShieldCheck size={16} className="shrink-0 mt-[1px]" style={{ color: "var(--yalola-primary)" }} aria-hidden />
          <span>{t.zero_tolerance} <LegalLink to="/legal/terms#zero-tolerance" testId="welcome-zero-tolerance-link">{t.terms}</LegalLink></span>
        </p>

        <p className="mt-4 text-xs leading-relaxed" style={{ color: "var(--yalola-text-muted)" }} data-testid="welcome-legal">
          {fill(t.legal, t)}
        </p>

        <div className="mt-4 flex items-center justify-center gap-1.5" role="group" aria-label={t.lang} data-testid="welcome-lang-row">
          {LANGUAGES.map((l) => {
            const on = lang === l.code;
            return (
              <button key={l.code} type="button" onClick={() => setLang(l.code)} className="flex items-center gap-1.5 h-9 px-3 rounded-full border text-xs font-bold transition-colors"
                style={{ borderColor: on ? "var(--yalola-primary)" : "var(--yalola-border)", background: on ? "var(--yalola-primary-soft)" : "var(--yalola-surface)", color: on ? "var(--yalola-primary)" : "var(--yalola-text-muted)" }}
                aria-pressed={on} data-testid={`welcome-lang-${l.code}`}>
                <Flag code={l.code} size={16} /> {l.code.toUpperCase()}
              </button>
            );
          })}
        </div>

        <button type="button" onClick={accept} className="mt-5 w-full h-12 rounded-full font-bold text-white flex items-center justify-center gap-1.5 active:scale-[0.98] transition-transform"
          style={{ background: "var(--yalola-primary)" }} data-testid="welcome-accept-btn">
          {t.accept} <ChevronRight size={18} />
        </button>

        <div className="mt-4 flex items-center justify-center gap-x-3 text-[11px]" style={{ color: "var(--yalola-text-muted)" }} data-testid="welcome-footer-links">
          <Link to="/legal/child-safety" className="underline" data-testid="welcome-child-safety-link">{t.child_safety}</Link>
          <span aria-hidden>·</span>
          <Link to="/supprimer-mon-compte" className="underline" data-testid="welcome-account-deletion-link">{t.account_deletion}</Link>
        </div>
      </div>
      <NavBarBackdrop />
    </div>
  );
}
